import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MessageSquare, Send, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const SMSSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [preferences, setPreferences] = useState({});
  const [testPhone, setTestPhone] = useState('');
  const [showTokens, setShowTokens] = useState({
    twilio_auth_token: false,
    vonage_api_secret: false,
    aws_secret_key: false
  });

  // Twilio state
  const [twilioSettings, setTwilioSettings] = useState({
    account_sid: '',
    auth_token: '',
    phone_number: '',
    mode: 'test',
    is_enabled: false,
    is_active: false
  });

  // Vonage state
  const [vonageSettings, setVonageSettings] = useState({
    api_key: '',
    api_secret: '',
    phone_number: '',
    mode: 'sandbox',
    is_enabled: false,
    is_active: false
  });

  // AWS SNS state
  const [awsSettings, setAwsSettings] = useState({
    access_key: '',
    secret_key: '',
    region: 'us-east-1',
    phone_number: '',
    mode: 'sandbox',
    is_enabled: false,
    is_active: false
  });

  useEffect(() => {
    fetchSettings();
    fetchPreferences();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/sms-settings`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });

      response.data.forEach(provider => {
        if (provider.provider === 'twilio') {
          setTwilioSettings({
            account_sid: provider.twilio_account_sid || '',
            auth_token: provider.twilio_auth_token || '',
            phone_number: provider.twilio_phone_number || '',
            mode: provider.twilio_mode || 'test',
            is_enabled: provider.is_enabled,
            is_active: provider.is_active
          });
        } else if (provider.provider === 'vonage') {
          setVonageSettings({
            api_key: provider.vonage_api_key || '',
            api_secret: provider.vonage_api_secret || '',
            phone_number: provider.vonage_phone_number || '',
            mode: provider.vonage_mode || 'sandbox',
            is_enabled: provider.is_enabled,
            is_active: provider.is_active
          });
        } else if (provider.provider === 'aws_sns') {
          setAwsSettings({
            access_key: provider.aws_access_key || '',
            secret_key: provider.aws_secret_key || '',
            region: provider.aws_region || 'us-east-1',
            phone_number: provider.aws_phone_number || '',
            mode: provider.aws_mode || 'sandbox',
            is_enabled: provider.is_enabled,
            is_active: provider.is_active
          });
        }
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching SMS settings:', error);
      console.warn('SMS settings not configured yet');
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API_URL}/sms-settings/preferences`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const saveTwilioSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/sms-settings/twilio`, twilioSettings, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      toast.success('Twilio settings saved successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error saving Twilio settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save Twilio settings');
    } finally {
      setSaving(false);
    }
  };

  const saveVonageSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/sms-settings/vonage`, vonageSettings, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      toast.success('Vonage settings saved successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error saving Vonage settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save Vonage settings');
    } finally {
      setSaving(false);
    }
  };

  const saveAWSSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/sms-settings/aws`, awsSettings, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      toast.success('AWS SNS settings saved successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error saving AWS settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save AWS SNS settings');
    } finally {
      setSaving(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/sms-settings/preferences`, preferences, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      toast.success('SMS preferences saved successfully');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const sendTestSMS = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }

    setSendingTest(true);
    try {
      const response = await axios.post(`${API_URL}/sms-settings/test`, 
        { phone_number: testPhone },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }}
      );
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error sending test SMS:', error);
      toast.error(error.response?.data?.error || 'Failed to send test SMS');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <MessageSquare className="w-8 h-8 text-primary-600" />
        <h2 className="text-2xl font-bold text-gray-900">SMS Settings</h2>
      </div>

      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
        <p className="text-sm text-blue-800 mb-2">
          <strong>💡 How It Works:</strong> Configure one SMS provider to send automatic text notifications to clients.
          Start with <strong>Twilio</strong> (easiest setup) or choose Vonage/AWS SNS based on your preference.
        </p>
        <p className="text-sm text-blue-800">
          <strong>🔄 Switching Providers:</strong> Only one provider can be active at a time. When you check "Set as Active" for a provider, 
          all others are automatically deactivated to prevent conflicts. You can safely switch between providers anytime.
        </p>
      </div>

      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          <strong>⚠️ Important:</strong> All providers require a <strong>purchased phone number</strong> (except AWS SNS). 
          You cannot send SMS without buying/renting a number from the provider. Free trials include credit to purchase numbers.
        </p>
      </div>

      {/* Twilio Settings */}
      <div className="card bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <h3 className="text-xl font-bold text-gray-900">Twilio</h3>
            <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded">Recommended</span>
            {twilioSettings.is_active && (
              <span className="px-2 py-1 text-xs font-semibold bg-blue-500 text-white rounded animate-pulse">
                ● ACTIVE
              </span>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={twilioSettings.is_enabled}
                onChange={(e) => setTwilioSettings({...twilioSettings, is_enabled: e.target.checked})}
                className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="font-medium text-gray-700">Enable</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={twilioSettings.is_active}
                onChange={(e) => setTwilioSettings({...twilioSettings, is_active: e.target.checked})}
                disabled={!twilioSettings.is_enabled}
                className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 disabled:opacity-50"
              />
              <span className="font-medium text-gray-700">Set as Active</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account SID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={twilioSettings.account_sid}
              onChange={(e) => setTwilioSettings({...twilioSettings, account_sid: e.target.value})}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">From Twilio Console Dashboard</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Auth Token <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showTokens.twilio_auth_token ? "text" : "password"}
                value={twilioSettings.auth_token}
                onChange={(e) => setTwilioSettings({...twilioSettings, auth_token: e.target.value})}
                placeholder="••••••••••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowTokens({...showTokens, twilio_auth_token: !showTokens.twilio_auth_token})}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showTokens.twilio_auth_token ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Click "Show" in Twilio Console</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={twilioSettings.phone_number}
              onChange={(e) => setTwilioSettings({...twilioSettings, phone_number: e.target.value})}
              placeholder="+15551234567"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">Your Twilio phone number</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mode <span className="text-red-500">*</span>
            </label>
            <select
              value={twilioSettings.mode}
              onChange={(e) => setTwilioSettings({...twilioSettings, mode: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="test">Test (Trial Account)</option>
              <option value="live">Live (Upgraded Account)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Use Test for trial accounts</p>
          </div>
        </div>

        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800 mb-4">
          <strong>⚠️ Test Mode:</strong> Can only send to verified numbers. Verify at: 
          <a href="https://console.twilio.com/us1/develop/phone-numbers/manage/verified" target="_blank" rel="noopener noreferrer" className="underline ml-1">
            Twilio Console
          </a>
        </div>

        <button
          onClick={saveTwilioSettings}
          disabled={saving}
          className="btn btn-primary"
        >
          {saving ? 'Saving...' : 'Save Twilio Settings'}
        </button>
      </div>

      {/* Vonage Settings */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <h3 className="text-xl font-bold text-gray-900">Vonage (Nexmo)</h3>
            {vonageSettings.is_active && (
              <span className="px-2 py-1 text-xs font-semibold bg-blue-500 text-white rounded animate-pulse">
                ● ACTIVE
              </span>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={vonageSettings.is_enabled}
                onChange={(e) => setVonageSettings({...vonageSettings, is_enabled: e.target.checked})}
                className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="font-medium text-gray-700">Enable</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={vonageSettings.is_active}
                onChange={(e) => setVonageSettings({...vonageSettings, is_active: e.target.checked})}
                disabled={!vonageSettings.is_enabled}
                className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 disabled:opacity-50"
              />
              <span className="font-medium text-gray-700">Set as Active</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <input
              type="text"
              value={vonageSettings.api_key}
              onChange={(e) => setVonageSettings({...vonageSettings, api_key: e.target.value})}
              placeholder="Your Vonage API Key"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Secret
            </label>
            <div className="relative">
              <input
                type={showTokens.vonage_api_secret ? "text" : "password"}
                value={vonageSettings.api_secret}
                onChange={(e) => setVonageSettings({...vonageSettings, api_secret: e.target.value})}
                placeholder="••••••••••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowTokens({...showTokens, vonage_api_secret: !showTokens.vonage_api_secret})}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showTokens.vonage_api_secret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="text"
              value={vonageSettings.phone_number}
              onChange={(e) => setVonageSettings({...vonageSettings, phone_number: e.target.value})}
              placeholder="+15551234567"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mode
            </label>
            <select
              value={vonageSettings.mode}
              onChange={(e) => setVonageSettings({...vonageSettings, mode: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="sandbox">Sandbox</option>
              <option value="live">Live</option>
            </select>
          </div>
        </div>

        <button
          onClick={saveVonageSettings}
          disabled={saving}
          className="btn btn-secondary"
        >
          {saving ? 'Saving...' : 'Save Vonage Settings'}
        </button>
      </div>

      {/* AWS SNS Settings */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <h3 className="text-xl font-bold text-gray-900">AWS SNS</h3>
            {awsSettings.is_active && (
              <span className="px-2 py-1 text-xs font-semibold bg-blue-500 text-white rounded animate-pulse">
                ● ACTIVE
              </span>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={awsSettings.is_enabled}
                onChange={(e) => setAwsSettings({...awsSettings, is_enabled: e.target.checked})}
                className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="font-medium text-gray-700">Enable</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={awsSettings.is_active}
                onChange={(e) => setAwsSettings({...awsSettings, is_active: e.target.checked})}
                disabled={!awsSettings.is_enabled}
                className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 disabled:opacity-50"
              />
              <span className="font-medium text-gray-700">Set as Active</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Access Key
            </label>
            <input
              type="text"
              value={awsSettings.access_key}
              onChange={(e) => setAwsSettings({...awsSettings, access_key: e.target.value})}
              placeholder="AKIAIOSFODNN7EXAMPLE"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Secret Key
            </label>
            <div className="relative">
              <input
                type={showTokens.aws_secret_key ? "text" : "password"}
                value={awsSettings.secret_key}
                onChange={(e) => setAwsSettings({...awsSettings, secret_key: e.target.value})}
                placeholder="••••••••••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowTokens({...showTokens, aws_secret_key: !showTokens.aws_secret_key})}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showTokens.aws_secret_key ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Region
            </label>
            <select
              value={awsSettings.region}
              onChange={(e) => setAwsSettings({...awsSettings, region: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="us-east-1">US East (N. Virginia)</option>
              <option value="us-west-2">US West (Oregon)</option>
              <option value="eu-west-1">EU (Ireland)</option>
              <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mode
            </label>
            <select
              value={awsSettings.mode}
              onChange={(e) => setAwsSettings({...awsSettings, mode: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          </div>
        </div>

        <button
          onClick={saveAWSSettings}
          disabled={saving}
          className="btn btn-secondary"
        >
          {saving ? 'Saving...' : 'Save AWS SNS Settings'}
        </button>
      </div>

      {/* Test SMS */}
      <div className="card bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <Send className="w-6 h-6 mr-2" />
          Send Test SMS
        </h3>

        <div className="flex space-x-3">
          <input
            type="tel"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="+15551234567"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          <button
            onClick={sendTestSMS}
            disabled={sendingTest || !testPhone}
            className="btn btn-primary"
          >
            {sendingTest ? 'Sending...' : 'Send Test'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Enter a phone number to test SMS delivery. In test mode, number must be verified first.
        </p>
      </div>

      {/* Notification Preferences */}
      <div className="card">
        <h3 className="text-xl font-bold text-gray-900 mb-4">SMS Notification Preferences</h3>
        <p className="text-sm text-gray-600 mb-4">
          Choose which appointment events trigger automatic SMS notifications to clients.
        </p>

        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.sms_appointment_confirmation || false}
              onChange={(e) => setPreferences({...preferences, sms_appointment_confirmation: e.target.checked})}
              className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
            />
            <div>
              <div className="font-medium text-gray-900">Appointment Confirmation</div>
              <div className="text-sm text-gray-500">Send SMS when appointment is booked/confirmed</div>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.sms_appointment_reminder || false}
              onChange={(e) => setPreferences({...preferences, sms_appointment_reminder: e.target.checked})}
              className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
            />
            <div>
              <div className="font-medium text-gray-900">Appointment Reminder</div>
              <div className="text-sm text-gray-500">Send SMS 24 hours before appointment</div>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.sms_appointment_cancellation || false}
              onChange={(e) => setPreferences({...preferences, sms_appointment_cancellation: e.target.checked})}
              className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
            />
            <div>
              <div className="font-medium text-gray-900">Appointment Cancellation</div>
              <div className="text-sm text-gray-500">Send SMS when appointment is cancelled</div>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.sms_appointment_status_change || false}
              onChange={(e) => setPreferences({...preferences, sms_appointment_status_change: e.target.checked})}
              className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
            />
            <div>
              <div className="font-medium text-gray-900">Status Change Notifications</div>
              <div className="text-sm text-gray-500">Send SMS when appointment status changes</div>
            </div>
          </label>
        </div>

        <button
          onClick={savePreferences}
          disabled={saving}
          className="btn btn-primary mt-4"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>

      {/* Help Section */}
      <div className="card bg-gray-50">
        <h3 className="text-lg font-bold text-gray-900 mb-3">📚 Setup Guides</h3>
        <div className="space-y-2 text-sm">
          <div>
            <strong>Twilio:</strong> 
            <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline ml-1">
              Sign up for free trial ($15 credit)
            </a>
          </div>
          <div>
            <strong>Vonage:</strong> 
            <a href="https://dashboard.nexmo.com/sign-up" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline ml-1">
              Create account (€2 free credit)
            </a>
          </div>
          <div>
            <strong>AWS SNS:</strong> 
            <a href="https://aws.amazon.com/sns/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline ml-1">
              AWS Simple Notification Service
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSSettings;
